import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import KeyboardVoiceRoundedIcon from "@mui/icons-material/KeyboardVoiceRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import { useSearchParams } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import { OwnerGoogleCalendarDialog } from "./calendar/OwnerGoogleCalendarDialogs";

function GooglePanel({ integration, loading, onOpen }) {
  const connected = Boolean(integration?.connected);
  const configured = Boolean(integration?.provider?.configured || integration?.configured);
  return <Paper variant="outlined" sx={{ p: { xs: 1.4, sm: 1.8 }, borderRadius: 2.7 }}>
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Box sx={{ width: 42, height: 42, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: connected ? "rgba(46,125,50,.10)" : "action.hover", color: connected ? "success.main" : "text.secondary" }}>
        <CalendarMonthRoundedIcon />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 1000 }}>Google Calendar</Typography>
          {loading ? <CircularProgress size={14} /> : <Chip size="small" color={connected ? "success" : configured ? "info" : "default"} label={connected ? "Conectado" : configured ? "Listo" : "Sin configurar"} sx={{ fontWeight: 900 }} />}
        </Stack>
        <Typography noWrap sx={{ mt: .15, color: "text.secondary", fontWeight: 700, fontSize: ".72rem" }}>{connected ? (integration?.accountEmail || integration?.calendarSummary || "Sincronización activa") : "OAuth, calendario principal y sincronización"}</Typography>
      </Box>
      <Button size="small" variant={connected ? "outlined" : "contained"} onClick={onOpen} sx={{ textTransform: "none", fontWeight: 950 }}>{connected ? "Administrar" : "Configurar"}</Button>
    </Stack>
  </Paper>;
}

function SiriPanel() {
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const [freshKey, setFreshKey] = React.useState("");
  const [testText, setTestText] = React.useState("¿Qué tengo hoy?");
  const [reply, setReply] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try { const response = await ownerApi.shortcutStatus(); setStatus(response.shortcut || null); }
    catch (err) { setError(formatApiError(err, "No pude leer el acceso de Siri / Atajos.")); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    setBusy(true); setError(""); setNotice(""); setFreshKey("");
    try {
      const response = await ownerApi.rotateShortcut();
      setFreshKey(response.credential?.key || "");
      setStatus((current) => ({ ...(current || {}), configured: true, active: true, keyPrefix: response.credential?.keyPrefix || current?.keyPrefix }));
      setNotice("Clave creada. Se muestra completa sólo ahora.");
    } catch (err) { setError(formatApiError(err, "No pude generar la clave del iPhone.")); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("¿Revocar el acceso del Atajo de iPhone? El atajo dejará de poder ejecutar acciones hasta generar una clave nueva.")) return;
    setBusy(true); setError(""); setNotice("");
    try { await ownerApi.revokeShortcut(); setFreshKey(""); setStatus((current) => ({ ...(current || {}), configured: true, active: false })); setNotice("Acceso revocado."); }
    catch (err) { setError(formatApiError(err, "No pude revocar el acceso.")); }
    finally { setBusy(false); }
  };

  const copy = async (value, message) => {
    try { await navigator.clipboard.writeText(value); setNotice(message); }
    catch { setNotice("Copiá el valor manualmente."); }
  };

  const test = async () => {
    if (!testText.trim()) return;
    setBusy(true); setError(""); setReply("");
    try { const response = await ownerApi.ownerActionCommand(testText.trim()); setReply(response.say || response.message || "Comando procesado."); }
    catch (err) { setError(formatApiError(err, "No pude probar el comando.")); }
    finally { setBusy(false); }
  };

  const active = Boolean(status?.active);
  const endpoint = "/api/owner-actions/command";

  return <Stack spacing={1.2}>
    {error ? <Alert severity="error">{error}</Alert> : null}
    {notice ? <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert> : null}

    <Paper variant="outlined" sx={{ p: { xs: 1.4, sm: 1.8 }, borderRadius: 2.7 }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ width: 42, height: 42, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: active ? "rgba(46,125,50,.10)" : "action.hover", color: active ? "success.main" : "text.secondary" }}><KeyboardVoiceRoundedIcon /></Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 1000 }}>Siri / Atajos</Typography>
            {loading ? <CircularProgress size={14} /> : <Chip size="small" color={active ? "success" : "default"} icon={active ? <CheckCircleRoundedIcon /> : <LinkOffRoundedIcon />} label={active ? "iPhone habilitado" : "Sin acceso"} sx={{ fontWeight: 900 }} />}
          </Stack>
          <Typography sx={{ mt: .15, color: "text.secondary", fontWeight: 700, fontSize: ".72rem" }}>Una clave independiente para hablar con WebProfit desde Atajos.</Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 1.4 }} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={.8}>
        <Button variant="contained" disabled={busy} startIcon={<KeyRoundedIcon />} onClick={rotate} sx={{ textTransform: "none", fontWeight: 950 }}>{active ? "Regenerar clave" : "Generar clave"}</Button>
        {active ? <Button color="error" variant="outlined" disabled={busy} startIcon={<DeleteOutlineRoundedIcon />} onClick={revoke} sx={{ textTransform: "none", fontWeight: 900 }}>Revocar</Button> : null}
      </Stack>

      {status?.keyPrefix && !freshKey ? <Typography sx={{ mt: 1, color: "text.secondary", fontSize: ".72rem", fontWeight: 750 }}>Clave activa: <Box component="span" sx={{ fontFamily: "monospace" }}>{status.keyPrefix}…</Box>{status?.lastUsedAt ? ` · último uso ${new Date(status.lastUsedAt).toLocaleString("es-AR")}` : ""}</Typography> : null}

      {freshKey ? <Box sx={{ mt: 1.2, p: 1.1, borderRadius: 2, bgcolor: "action.hover" }}>
        <Typography sx={{ fontSize: ".68rem", color: "text.secondary", fontWeight: 900, mb: .45 }}>CLAVE DEL ATAJO · COPIALA AHORA</Typography>
        <Stack direction="row" spacing={.5} alignItems="center">
          <Typography sx={{ minWidth: 0, flex: 1, overflowWrap: "anywhere", fontFamily: "monospace", fontWeight: 800, fontSize: ".72rem" }}>{freshKey}</Typography>
          <Tooltip title="Copiar clave"><IconButton size="small" onClick={() => copy(freshKey, "Clave copiada.")}><ContentCopyRoundedIcon fontSize="small" /></IconButton></Tooltip>
        </Stack>
      </Box> : null}
    </Paper>

    <Paper variant="outlined" sx={{ p: { xs: 1.4, sm: 1.8 }, borderRadius: 2.7 }}>
      <Typography sx={{ fontWeight: 1000, mb: .8 }}>Configurar el Atajo</Typography>
      <Stack spacing={0.75}>
        <TextField size="small" label="Endpoint" value={endpoint} InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => copy(endpoint, "Endpoint copiado.")}><ContentCopyRoundedIcon fontSize="small" /></IconButton></InputAdornment> }} />
        <Typography sx={{ color: "text.secondary", fontSize: ".72rem", fontWeight: 700 }}>POST · Header <Box component="span" sx={{ fontFamily: "monospace" }}>x-webprofit-shortcut-key</Box> · Body JSON con <Box component="span" sx={{ fontFamily: "monospace" }}>text</Box>.</Typography>
      </Stack>
    </Paper>

    <Paper variant="outlined" sx={{ p: { xs: 1.4, sm: 1.8 }, borderRadius: 2.7 }}>
      <Typography sx={{ fontWeight: 1000, mb: .8 }}>Probar motor</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={.7}>
        <TextField fullWidth size="small" value={testText} onChange={(e) => setTestText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); test(); } }} placeholder="Gasté 15 mil en nafta" />
        <Button variant="contained" disabled={busy || !testText.trim()} startIcon={<PlayArrowRoundedIcon />} onClick={test} sx={{ textTransform: "none", fontWeight: 950, minWidth: 110 }}>Probar</Button>
      </Stack>
      {reply ? <Alert severity="info" sx={{ mt: .9 }}>{reply}</Alert> : null}
    </Paper>
  </Stack>;
}

export default function OwnerSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("seccion") === "siri" ? "siri" : "google";
  const [tab, setTab] = React.useState(requested);
  const [googleOpen, setGoogleOpen] = React.useState(false);
  const [googleIntegration, setGoogleIntegration] = React.useState(null);
  const [googleLoading, setGoogleLoading] = React.useState(true);
  const [googleError, setGoogleError] = React.useState("");

  const loadGoogle = React.useCallback(async () => {
    setGoogleLoading(true); setGoogleError("");
    try { const response = await ownerApi.googleCalendarStatus(); setGoogleIntegration(response.integration || null); }
    catch (err) { setGoogleError(formatApiError(err, "No pude leer Google Calendar.")); }
    finally { setGoogleLoading(false); }
  }, []);
  React.useEffect(() => { loadGoogle(); }, [loadGoogle]);

  const changeTab = (_, next) => { setTab(next); const nextParams = new URLSearchParams(); if (next === "siri") nextParams.set("seccion", "siri"); setSearchParams(nextParams, { replace: true }); };

  return <Box sx={{ width: "100%", maxWidth: 920, mx: "auto" }}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={.8} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1 }}>
      <Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 1050, fontSize: { xs: "1.12rem", sm: "1.25rem" } }}>Configuración</Typography><Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".73rem" }}>Conexiones externas del Owner.</Typography></Box>
      <Tabs value={tab} onChange={changeTab} variant="scrollable" scrollButtons={false} sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 950, px: 1.2 } }}>
        <Tab value="google" icon={<CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Google" />
        <Tab value="siri" icon={<KeyboardVoiceRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Siri" />
      </Tabs>
    </Stack>

    {tab === "google" ? <Stack spacing={1.2}>
      {googleError ? <Alert severity="error">{googleError}</Alert> : null}
      <GooglePanel integration={googleIntegration} loading={googleLoading} onOpen={() => setGoogleOpen(true)} />
      <Paper variant="outlined" sx={{ p: { xs: 1.4, sm: 1.8 }, borderRadius: 2.7 }}>
        <Typography sx={{ fontWeight: 1000 }}>Agenda conectada</Typography>
        <Typography sx={{ mt: .25, color: "text.secondary", fontWeight: 700, fontSize: ".73rem" }}>La Agenda usa esta conexión para leer y crear eventos. El OAuth, calendario elegido, sincronización y pruebas se administran acá.</Typography>
      </Paper>
    </Stack> : <SiriPanel />}

    <OwnerGoogleCalendarDialog open={googleOpen} onClose={() => setGoogleOpen(false)} currentIntegration={googleIntegration} onChanged={loadGoogle} />
  </Box>;
}
